import type * as React from 'react';

import { brandIcons, BRAND_LABELS, type BrandIconId } from '../../assets/brandIcons';
import { cn } from '../../lib/utils';

/** Default square size in pixels (matches the 28×28 mock). */
const DEFAULT_SIZE = 28;

export interface ConnectedServiceIconProps {
  /** Which SaaS brand icon to render. */
  service: BrandIconId;
  /** Accessible name. Defaults to the brand's registered label. */
  label?: string;
  /** Square pixel size (applied to both width and height). Defaults to 28. */
  size?: number;
  /** Extra Tailwind/utility classes merged onto the root element. */
  className?: string;
}

/**
 * Renders the brand icon for one connected SaaS service. Purely presentational —
 * the SVG comes from `brandIcons`, so the only styling dial the consumer has is
 * size and className. Always exposes a non-empty accessible name.
 */
export const ConnectedServiceIcon: React.FC<ConnectedServiceIconProps> = ({
  service,
  label,
  size = DEFAULT_SIZE,
  className,
}) => {
  const accessibleName = label ?? BRAND_LABELS[service];

  return (
    <img
      src={brandIcons[service]}
      alt={accessibleName}
      width={size}
      height={size}
      className={cn('inline-block shrink-0', className)}
    />
  );
};

ConnectedServiceIcon.displayName = 'ConnectedServiceIcon';
